import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Download,
  Search,
  AlertTriangle,
  Link2,
  Check,
  X,
  Pencil,
  Copy,
  History,
  XCircle,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Truck,
  Ship,
  Plane,
  Train,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { PageHeader, MetricCard } from '@/components/common';
import {
  rateEngineApi,
  CarrierRate,
  RateFilterDto,
  ChargeCategory,
  TransportMode,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { format } from 'date-fns';

const MODE_ICONS: Record<string, React.ReactNode> = {
  Ocean: <Ship className="w-4 h-4" />,
  Air: <Plane className="w-4 h-4" />,
  TruckFTL: <Truck className="w-4 h-4" />,
  TruckLTL: <Truck className="w-4 h-4" />,
  Rail: <Train className="w-4 h-4" />,
  Intermodal: <Truck className="w-4 h-4" />,
};

const CHARGE_CATEGORIES: { value: ChargeCategory; label: string }[] = [
  { value: 'BASE_FREIGHT', label: 'Base Freight' },
  { value: 'FUEL', label: 'Fuel' },
  { value: 'ACCESSORIAL', label: 'Accessorial' },
  { value: 'HANDLING', label: 'Handling' },
  { value: 'DOCUMENTATION', label: 'Documentation' },
  { value: 'CUSTOMS', label: 'Customs' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'OTHER', label: 'Other' },
];

const TRANSPORT_MODES: { value: TransportMode; label: string }[] = [
  { value: 'Ocean', label: 'Ocean' },
  { value: 'Air', label: 'Air' },
  { value: 'TruckFTL', label: 'Truck FTL' },
  { value: 'TruckLTL', label: 'Truck LTL' },
  { value: 'Rail', label: 'Rail' },
  { value: 'Intermodal', label: 'Intermodal' },
];

export default function Rates() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [rates, setRates] = useState<CarrierRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);

  // Filters from URL
  const filters: RateFilterDto = {
    page: parseInt(searchParams.get('page') || '1'),
    limit: parseInt(searchParams.get('limit') || '50'),
    search: searchParams.get('search') || '',
    carrierId: searchParams.get('carrierId') || undefined,
    mode: (searchParams.get('mode') as TransportMode) || undefined,
    chargeCategory: (searchParams.get('chargeCategory') as ChargeCategory) || undefined,
    activeOnly: searchParams.get('activeOnly') === 'true' || undefined,
    expiringSoon: searchParams.get('expiringSoon') === 'true' || undefined,
    contractOnly: searchParams.get('contractOnly') === 'true' || undefined,
  };

  // Modal states
  const [expireModalOpen, setExpireModalOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState<CarrierRate | null>(null);
  const [expiring, setExpiring] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const setFilter = (key: keyof RateFilterDto, value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    // Reset page when filters change
    if (key !== 'page') {
      params.set('page', '1');
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters =
    filters.search ||
    filters.carrierId ||
    filters.mode ||
    filters.chargeCategory ||
    filters.activeOnly ||
    filters.expiringSoon ||
    filters.contractOnly;

  const loadRates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rateEngineApi.getRates(filters);
      // Handle both paginated { data: [] } and direct array responses
      const ratesData = Array.isArray(response) ? response : (response?.data || []);
      setRates(ratesData);
      setTotalCount(response?.pagination?.total ?? ratesData.length);
    } catch (error) {
      console.error('Failed to load rates:', error);
      // Set empty array on error to prevent crashes
      setRates([]);
      setTotalCount(0);
      // Only show toast for non-404 errors (backend may not be implemented yet)
      const errorMessage = getErrorMessage(error);
      if (!errorMessage.includes('404') && !errorMessage.includes('Not Found')) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  // Calculate counts
  const activeCount = useMemo(
    () => rates.filter((r) => r.isActive).length,
    [rates]
  );

  const expiringCount = useMemo(() => {
    return rates.filter((r) => {
      if (!r.expirationDate || !r.isActive) return false;
      const daysUntil = Math.ceil(
        (new Date(r.expirationDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      );
      return daysUntil > 0 && daysUntil <= 30;
    }).length;
  }, [rates]);

  const contractCount = useMemo(
    () => rates.filter((r) => r.isContractRate).length,
    [rates]
  );

  // Copy shareable link
  const handleCopyLink = useCallback(() => {
    const shareUrl = window.location.href;
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        setLinkCopied(true);
        toast.success('Link copied! Share this URL to show others this filtered view.');
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(() => {
        toast.error('Failed to copy link');
      });
  }, []);

  const handleDuplicate = async (rate: CarrierRate) => {
    try {
      await rateEngineApi.duplicateRate(rate.id);
      toast.success('Rate duplicated successfully');
      loadRates();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleExpireClick = (rate: CarrierRate) => {
    setSelectedRate(rate);
    setExpireModalOpen(true);
  };

  const handleExpireConfirm = async () => {
    if (!selectedRate) return;

    setExpiring(true);
    try {
      await rateEngineApi.updateRate(selectedRate.id, {
        expirationDate: new Date().toISOString(),
        isActive: false,
      });
      setExpireModalOpen(false);
      setSelectedRate(null);
      loadRates();
      toast.success('Rate expired successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setExpiring(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const headers = [
        'SKU',
        'Carrier',
        'Lane',
        'Rate Type',
        'Base Rate',
        'Effective Date',
        'Expiration Date',
        'Status',
      ];

      const rows = rates.map((rate) => [
        rate.rateSKU || '',
        rate.carrier?.name || '',
        rate.lane
          ? `${rate.lane.originCity}, ${rate.lane.originState} → ${rate.lane.destCity}, ${rate.lane.destState}`
          : '',
        rate.rateType,
        rate.baseRate.toFixed(2),
        format(new Date(rate.effectiveDate), 'MM/dd/yyyy'),
        rate.expirationDate
          ? format(new Date(rate.expirationDate), 'MM/dd/yyyy')
          : '',
        rate.isActive ? 'Active' : 'Inactive',
      ]);

      const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rates-export-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Exported ${rates.length} rates to CSV`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(rates.map((r) => r.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRate = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const totalPages = Math.ceil(totalCount / (filters.limit || 50));

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Rate Management"
        description="Manage freight rates, child charges, and slab pricing"
        actions={
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button variant="ghost" onClick={handleCopyLink} title="Copy shareable link">
                {linkCopied ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4 mr-2" />
                    Share View
                  </>
                )}
              </Button>
            )}
            <Button variant="outline" onClick={handleExport} disabled={exporting}>
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Rate
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="bg-card rounded-xl border p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by SKU, carrier, lane..."
              value={filters.search || ''}
              onChange={(e) => setFilter('search', e.target.value || undefined)}
              className="pl-10 pr-10"
            />
            {filters.search && (
              <button
                onClick={() => setFilter('search', undefined)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <Select
            value={filters.mode || '_all_'}
            onValueChange={(v) => setFilter('mode', v === '_all_' ? undefined : v)}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Modes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all_">All Modes</SelectItem>
              {TRANSPORT_MODES.map((mode) => (
                <SelectItem key={mode.value} value={mode.value}>
                  {mode.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.chargeCategory || '_all_'}
            onValueChange={(v) => setFilter('chargeCategory', v === '_all_' ? undefined : v)}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all_">All Categories</SelectItem>
              {CHARGE_CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={filters.activeOnly || false}
                onCheckedChange={(checked) =>
                  setFilter('activeOnly', checked ? 'true' : undefined)
                }
              />
              Active Only
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={filters.expiringSoon || false}
                onCheckedChange={(checked) =>
                  setFilter('expiringSoon', checked ? 'true' : undefined)
                }
              />
              Expiring Soon
              {expiringCount > 0 && (
                <Badge variant="secondary" className="bg-amber-100 text-amber-600">
                  {expiringCount}
                </Badge>
              )}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={filters.contractOnly || false}
                onCheckedChange={(checked) =>
                  setFilter('contractOnly', checked ? 'true' : undefined)
                }
              />
              Contract Only
              {contractCount > 0 && (
                <Badge variant="secondary" className="bg-blue-100 text-blue-600">
                  {contractCount}
                </Badge>
              )}
            </label>
          </div>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-destructive hover:text-destructive"
            >
              <X className="w-4 h-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-5 gap-4">
        <MetricCard label="Total Rates" value={totalCount} />
        <MetricCard
          label="Active"
          value={activeCount}
          trend={
            activeCount > 0
              ? { direction: 'up', value: '', text: 'Active rates' }
              : undefined
          }
        />
        <MetricCard
          label="Expiring Soon"
          value={expiringCount}
          trend={
            expiringCount > 0
              ? { direction: 'down', value: '', text: 'Within 30 days' }
              : undefined
          }
        />
        <MetricCard label="Contract Rates" value={contractCount} />
        <MetricCard label="Conflicts" value={0} />
      </div>

      {/* Rate Table */}
      <div className="bg-card rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50 text-left">
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    checked={
                      rates.length > 0 && selectedIds.size === rates.length
                    }
                    onCheckedChange={handleSelectAll}
                  />
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  SKU
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Carrier
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Lane
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Mode
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Category
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                  Base Rate
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Effective
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </td>
                </tr>
              ) : rates.length > 0 ? (
                rates.map((rate) => {
                  const isExpiring =
                    rate.expirationDate &&
                    rate.isActive &&
                    Math.ceil(
                      (new Date(rate.expirationDate).getTime() - Date.now()) /
                        (1000 * 60 * 60 * 24)
                    ) <= 30;

                  return (
                    <tr
                      key={rate.id}
                      className={`hover:bg-muted/50 ${isExpiring ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={selectedIds.has(rate.id)}
                          onCheckedChange={(checked) =>
                            handleSelectRate(rate.id, !!checked)
                          }
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm">
                          {rate.rateSKU || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{rate.carrier?.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {rate.carrier?.code}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {rate.lane ? (
                          <div className="text-sm">
                            {rate.lane.originCity}, {rate.lane.originState} →{' '}
                            {rate.lane.destCity}, {rate.lane.destState}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {rate.mode && (
                          <div className="flex items-center gap-1.5">
                            {MODE_ICONS[rate.mode]}
                            <span className="text-sm">{rate.mode}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">
                          {CHARGE_CATEGORIES.find(
                            (c) => c.value === rate.chargeCategory
                          )?.label || rate.chargeCategory}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-medium">
                          ${rate.baseRate.toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {rate.rateType} • {rate.currency}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {format(new Date(rate.effectiveDate), 'MMM d, yyyy')}
                        </div>
                        {rate.expirationDate && (
                          <div
                            className={`text-xs ${isExpiring ? 'text-amber-600' : 'text-muted-foreground'}`}
                          >
                            Exp: {format(new Date(rate.expirationDate), 'MMM d, yyyy')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <Badge
                            variant="outline"
                            className={
                              rate.isActive
                                ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                                : 'bg-gray-50 text-gray-600 border-gray-200'
                            }
                          >
                            {rate.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          {rate.isContractRate && (
                            <Badge
                              variant="outline"
                              className="bg-purple-50 text-purple-600 border-purple-200"
                            >
                              Contract
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDuplicate(rate)}
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <History className="w-4 h-4 mr-2" />
                              Version History
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {rate.isActive && (
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleExpireClick(rate)}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Expire Rate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} className="py-12 text-center">
                    <AlertTriangle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Rates Found</h3>
                    <p className="text-muted-foreground">
                      No rates match your current filters.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="text-sm text-muted-foreground">
              Showing {(filters.page! - 1) * filters.limit! + 1} -{' '}
              {Math.min(filters.page! * filters.limit!, totalCount)} of{' '}
              {totalCount} rates
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={filters.page === 1}
                onClick={() => setFilter('page', String(filters.page! - 1))}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <Button
                    key={page}
                    variant={filters.page === page ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setFilter('page', String(page))}
                  >
                    {page}
                  </Button>
                );
              })}
              {totalPages > 5 && (
                <>
                  <span className="px-2 text-muted-foreground">...</span>
                  <Button
                    variant={filters.page === totalPages ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setFilter('page', String(totalPages))}
                  >
                    {totalPages}
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                disabled={filters.page === totalPages}
                onClick={() => setFilter('page', String(filters.page! + 1))}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-card border rounded-xl shadow-lg p-4 flex items-center gap-4">
          <span className="text-sm font-medium">
            {selectedIds.size} rate(s) selected
          </span>
          <Button variant="outline" size="sm">
            <Copy className="w-4 h-4 mr-2" />
            Duplicate
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button variant="destructive" size="sm">
            <XCircle className="w-4 h-4 mr-2" />
            Expire
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedIds(new Set())}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Expire Confirmation Modal */}
      <Dialog open={expireModalOpen} onOpenChange={setExpireModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              Expire Rate?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to expire this rate? This will set the
              expiration date to today and mark the rate as inactive.
            </DialogDescription>
          </DialogHeader>

          {selectedRate && (
            <div className="p-3 bg-muted rounded-lg text-sm">
              <div className="font-medium">{selectedRate.rateSKU || 'No SKU'}</div>
              <div className="text-muted-foreground">
                {selectedRate.carrier?.name} - ${selectedRate.baseRate.toFixed(2)}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setExpireModalOpen(false);
                setSelectedRate(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleExpireConfirm}
              disabled={expiring}
            >
              {expiring ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Expire Rate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
